/**
 * src/components/spatial/SpatialPresenceSections.tsx
 *
 * The AI Presence and Media / Live sections of the spatial object
 * editor.
 *
 * Kept in its own file rather than inlined into the editor tab: these
 * two sections own their own load/save cycle against GeoLink, and
 * folding that into the object form would mean an unsaved persona could
 * be lost by a geometry validation failure that has nothing to do with it.
 *
 * Presence can only be configured on an object that already exists, and
 * only by a wallet the contract-backed authorization service says may
 * manage it. That wallet is recorded as the standing authority for
 * anything the object later does on its own; the server re-verifies it
 * per action, so a revoked wallet stops autonomous behaviour rather than
 * leaving it running on a stale grant.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { geoLinkSpatialObjectsAdminClient } from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import {
  DEFAULT_AGENT_CONFIG,
  DEFAULT_MEDIA_CONFIG,
  DEFAULT_SURFACE_CONFIG,
} from '../../services/spatial/spatialPresenceTypes';
import type {
  SpatialBroadcastAnchorType,
  SpatialBroadcastTriggerMode,
  SpatialInteractionMode,
  SpatialObjectAgentConfig,
  SpatialObjectAgentConfigPatch,
  SpatialObjectMediaAsset,
  SpatialObjectMediaConfig,
  SpatialObjectMediaConfigPatch,
  SpatialObjectSurfaceConfig,
  SpatialObjectSurfaceConfigPatch,
  SpatialSurfaceActivationMode,
  SpatialSurfaceKind,
  SpatialTtsInterruptMode,
  SpatialVolumeCurve,
} from '../../services/spatial/spatialPresenceTypes';
import type { ContractExecutionRuleSummary } from '../../services/spatial/geoLinkTypes';
import { activateSpatialSurface } from '../../services/spatial/spatialPresenceClient';
import {
  flagContentRef,
  getFlagReading,
  parseFlagContentRef,
} from '../../utils/countryFlagPalette';
import {
  flagArrangements,
  type FlagArrangement,
  type FlagLayout,
  type FlagReading,
} from '../../utils/flagArrangements';
import { countryChoices } from '../../utils/countryChoices';
import { failureText } from './spatialBlockerText';
import { rememberFlagIntent } from './spatialFlagIntentCache';
import SpatialObjectTreasurySection from './SpatialObjectTreasurySection';
import { surfaceNumbersComplaint } from './spatialSurfaceValidation';

/** Voice ids the Azure catalogue ships with; any valid id may be typed. */
const VOICE_HINTS = [
  'en-US-JennyNeural',
  'en-US-GuyNeural',
  'en-US-AriaNeural',
  'en-GB-RyanNeural',
  'en-GB-SoniaNeural',
  'ar-AE-FatimaNeural',
  'fr-FR-DeniseNeural',
  'de-DE-KatjaNeural',
  'ja-JP-NanamiNeural',
];

const INTERACTION_MODES: SpatialInteractionMode[] =
  ['passive', 'proximity', 'explicit', 'event', 'execution_rule'];

const TRIGGER_MODES: SpatialBroadcastTriggerMode[] =
  ['manual', 'proximity', 'event', 'execution_rule', 'ai_action'];

const ANCHOR_TYPES: SpatialBroadcastAnchorType[] =
  ['centroid', 'entrance', 'point', 'floor', 'zone', 'custom'];

const INTERRUPT_MODES: SpatialTtsInterruptMode[] = ['queue', 'interrupt', 'replace'];
const VOLUME_CURVES: SpatialVolumeCurve[] = ['none', 'linear', 'smoothstep'];

const SURFACE_KINDS: SpatialSurfaceKind[] = ['facade', 'screen', 'projection', 'lighting'];

const SURFACE_MODES: SpatialSurfaceActivationMode[] =
  ['manual', 'event', 'execution_rule', 'ai_action', 'proximity'];

/** Every country, by name, for the flag picker. */
const COUNTRY_CHOICES = countryChoices();

/** What is stored under `metadata.surface_flag`, as far as this editor cares. */
export interface StoredFlagIntent {
  countryCode?: unknown;
  arrangement?: unknown;
  bands?: unknown;
}

type AgentDraft = SpatialObjectAgentConfigPatch;
type MediaDraft = SpatialObjectMediaConfigPatch;
type SurfaceDraft = SpatialObjectSurfaceConfigPatch;

/** The allow-list is edited as one id per line; it is stored as an array. */
function contentRefsToText(refs: SurfaceDraft['allowedContentRefs']): string {
  if (!Array.isArray(refs)) return '';
  return refs.map((r) => (typeof r === 'string' ? r : r.ref)).join('\n');
}

function textToContentRefs(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

export interface SpatialPresenceSectionsProps {
  /** Empty for an unsaved object; presence is hidden until it has an id. */
  objectId: string;
  objectName: string | null;
  walletAddress: string | null;
  /** May this operator change the object's settings? False disables everything. */
  canManage: boolean;
  /** Still resolving authority — controls stay disabled rather than guessing. */
  authLoading: boolean;
  /**
   * Whether the connected wallet holds the chain-side manage authority the
   * runtime re-checks before the object acts on its own.
   *
   * Separate from `canManage` because they answer different questions.
   * Configuring presence is editing; acting on it is authority. An object
   * the chain has never heard of can be configured now and will simply
   * stay silent until it is registered — which is worth saying out loud,
   * since the alternative is an operator ticking boxes that never do
   * anything and no page telling them why.
   */
  canActAutonomously?: boolean;
  rules: ContractExecutionRuleSummary[];
  /**
   * The object's country, used to pick the flag without asking. Comes from
   * GeoLink's `country_code` when it is set, and from the centroid
   * resolver when it is not.
   */
  countryCode?: string | null;
  /**
   * The flag already recorded on the object, so reopening the editor shows
   * the arrangement that is actually being painted rather than resetting
   * to the recommended one.
   */
  storedFlag?: StoredFlagIntent | null;
  /**
   * Signs a server-built envelope, for the one thing on this page that
   * is a chain write rather than a GeoLink config change: a beneficiary
   * collecting what the object holds. Absent, the treasury reads but
   * offers no payout.
   */
  signXdr?: ((xdr: string, latestLedger?: number) => Promise<string>) | null;
}

/** A labelled checkbox in the editor's row layout. */
const Check: React.FC<{
  label: string;
  checked: boolean;
  disabled?: boolean;
  hint?: string;
  onChange: (v: boolean) => void;
}> = ({ label, checked, disabled, hint, onChange }) => (
  <div className="spatial-admin-row">
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: disabled ? 'default' : 'pointer' }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
    {hint && <div className="spatial-admin-helper">{hint}</div>}
  </div>
);

/**
 * A miniature of the building wearing one arrangement.
 *
 * Tapered, and read top-down like the tower: bands stack, columns stand
 * side by side under whatever crowns them. A preview that showed a
 * vertical arrangement as stacked bands would be a picture of a different
 * building from the one the operator is about to approve.
 */
const ArrangementPreview: React.FC<{ layout: FlagLayout }> = ({ layout }) => {
  const shell: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    width: 26,
    height: 62,
    overflow: 'hidden',
    border: '1px solid #cbd5e1',
    borderRadius: '3px 3px 0 0',
    clipPath: 'polygon(30% 0, 70% 0, 100% 100%, 0 100%)',
  };

  if (layout.kind === 'bands') {
    return (
      <span aria-hidden style={shell}>
        {layout.bands.map((c, i) => (
          <span key={`${c}-${i}`} style={{ background: c, flex: 1 }} />
        ))}
      </span>
    );
  }

  return (
    <span aria-hidden style={shell}>
      {layout.crown && (
        <span
          style={{
            background: layout.crown,
            flex: `0 0 ${Math.round(layout.crownShare * 100)}%`,
          }}
        />
      )}
      <span style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
        {layout.columns.map((c, i) => (
          <span key={`${c}-${i}`} style={{ background: c, flex: 1 }} />
        ))}
      </span>
    </span>
  );
};

/** How an arrangement will meet the building, in a sentence. */
function layoutShapeNote(layout: FlagLayout): string {
  if (layout.kind === 'bands') {
    const n = layout.bands.length;
    return `${n} band${n === 1 ? '' : 's'}, the first landing on the highest part of the building.`;
  }
  const n = layout.columns.length;
  const columns = `${n} column${n === 1 ? '' : 's'} running down the facade side by side`;
  return layout.crown
    ? `${columns}, under a crown taking the top ${Math.round(layout.crownShare * 100)}% of the height.`
    : `${columns}.`;
}

/**
 * A number field, optionally clearable.
 *
 * `Number('')` is 0, so an emptied box used to save a *measurement of
 * zero* — a surface 0 m wide, which GeoLink refuses. For fields that
 * genuinely mean "unspecified", clearing the box has to send null.
 */
type NumberRowProps = {
  label: string;
  value: number | null | undefined;
  disabled?: boolean;
  hint?: string;
  min?: number;
  step?: number;
} & (
  | { clearable?: false; onChange: (v: number) => void }
  /** Only a field that genuinely means "unspecified" may report null. */
  | { clearable: true; onChange: (v: number | null) => void }
);

const NumberRow: React.FC<NumberRowProps> = (props) => {
  const { label, value, disabled, hint, min, step } = props;
  return (
    <div className="spatial-admin-row">
      <label>{label}</label>
      <input
        className="spatial-admin-input"
        type="number"
        min={min}
        step={step}
        value={value ?? ''}
        disabled={disabled}
        placeholder={props.clearable ? 'not set' : undefined}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '' && props.clearable) { props.onChange(null); return; }
          props.onChange(Number(raw));
        }}
      />
      {hint && <div className="spatial-admin-helper">{hint}</div>}
    </div>
  );
};

/**
 * Says what happened where it happened.
 *
 * These three sections make one long scroll, so a single banner at the top
 * is off-screen by the time an operator reaches the surface controls — a
 * refused save then looks exactly like a button that does nothing.
 */
const Feedback: React.FC<{ error: string | null; notice: string | null }> = ({ error, notice }) => {
  if (!error && !notice) return null;
  return (
    <div
      className="spatial-admin-helper"
      role={error ? 'alert' : 'status'}
      style={{ color: error ? '#b91c1c' : '#047857', marginTop: 6 }}
    >
      {error || notice}
    </div>
  );
};

const RuleSelect: React.FC<{
  label: string;
  value: number | null | undefined;
  rules: ContractExecutionRuleSummary[];
  disabled?: boolean;
  hint?: string;
  unbindable?: readonly number[];
  onChange: (v: number | null) => void;
}> = ({ label, value, rules, disabled, hint, unbindable, onChange }) => (
  <div className="spatial-admin-row">
    <label>{label}</label>
    <select
      className="spatial-admin-select"
      value={value == null ? '' : String(value)}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">— None —</option>
      {rules.map((r) => {
        const blocked = unbindable?.includes(Number(r.id)) ?? false;
        return (
          <option key={String(r.id)} value={String(r.id)} disabled={blocked}>
            #{r.id} · {r.rule_name ?? r.ruleName ?? '(unnamed)'} · {r.function_name ?? r.functionName ?? '?'}
            {blocked ? ' · other account' : ''}
          </option>
        );
      })}
    </select>
    {hint && <div className="spatial-admin-helper">{hint}</div>}
  </div>
);

/** Strip the client's internal prefix so an operator reads the cause, not the call site. */
const humanError = (e: unknown): string =>
  (e instanceof Error ? e.message : String(e))
    .replace(/^\[geoLinkSpatialAdmin\]\s*\S+:\s*/, '');

/**
 * GeoLink lists every rule visible to the connected wallet, but only lets a
 * caller bind rules owned by the account behind our API key — so a rule can
 * be offered by the dropdown and still be refused on save. The two sets
 * cannot be told apart in advance: GeoLink exposes no caller identity to an
 * API-key client, and the list carries no field that predicts the check.
 */
const isRuleOwnershipRefusal = (e: unknown): boolean =>
  (e as { status?: number } | null)?.status === 403
  && /another user'?s execution rule/i.test(e instanceof Error ? e.message : '');

export const SpatialPresenceSections: React.FC<SpatialPresenceSectionsProps> = ({
  objectId,
  objectName,
  walletAddress,
  canManage,
  authLoading,
  canActAutonomously = true,
  rules,
  countryCode = null,
  storedFlag = null,
  signXdr = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Which section the current message belongs beside; null means the load. */
  const [msgScope, setMsgScope] = useState<'agent' | 'media' | 'surface' | null>(null);
  const [unbindableRules, setUnbindableRules] = useState<readonly number[]>([]);

  const [agentSaved, setAgentSaved] = useState<SpatialObjectAgentConfig | null>(null);
  const [mediaSaved, setMediaSaved] = useState<SpatialObjectMediaConfig | null>(null);
  const [assets, setAssets] = useState<SpatialObjectMediaAsset[]>([]);

  const [surfaceSaved, setSurfaceSaved] = useState<SpatialObjectSurfaceConfig | null>(null);

  const [agent, setAgent] = useState<AgentDraft>(DEFAULT_AGENT_CONFIG);
  const [media, setMedia] = useState<MediaDraft>(DEFAULT_MEDIA_CONFIG);
  const [surface, setSurface] = useState<SurfaceDraft>(DEFAULT_SURFACE_CONFIG);

  const disabled = !canManage || authLoading || loading;

  const load = useCallback(async () => {
    if (!objectId) return;
    setLoading(true);
    setError(null);
    try {
      const [agentCfg, mediaBundle, surfaceBundle] = await Promise.all([
        geoLinkSpatialObjectsAdminClient.getAgentConfig(objectId),
        geoLinkSpatialObjectsAdminClient.getMediaConfig(objectId),
        geoLinkSpatialObjectsAdminClient.getSurfaceConfig(objectId),
      ]);
      setAgentSaved(agentCfg);
      setMediaSaved(mediaBundle.config);
      setSurfaceSaved(surfaceBundle.config);
      setAssets(mediaBundle.assets);
      // A missing row and a saved-but-disabled row look the same in the
      // form, so the defaults stand in only for the former.
      setAgent(agentCfg ?? DEFAULT_AGENT_CONFIG);
      setMedia(mediaBundle.config ?? DEFAULT_MEDIA_CONFIG);
      setSurface(surfaceBundle.config ?? DEFAULT_SURFACE_CONFIG);
    } catch (e) {
      setError(humanError(e));
    } finally {
      setLoading(false);
    }
  }, [objectId]);

  useEffect(() => { void load(); }, [load]);

  const setA = useCallback(<K extends keyof AgentDraft>(k: K, v: AgentDraft[K]) => {
    setAgent((prev) => ({ ...prev, [k]: v }));
  }, []);
  const setM = useCallback(<K extends keyof MediaDraft>(k: K, v: MediaDraft[K]) => {
    setMedia((prev) => ({ ...prev, [k]: v }));
  }, []);
  const setS = useCallback(<K extends keyof SurfaceDraft>(k: K, v: SurfaceDraft[K]) => {
    setSurface((prev) => ({ ...prev, [k]: v }));
  }, []);

  /**
   * A rule we cannot bind would otherwise fail every future save of the
   * section, so the selection is dropped and the id remembered — the whole
   * form is then savable again without the operator having to guess which
   * field GeoLink objected to.
   */
  const reportSaveFailure = useCallback((
    e: unknown,
    ruleId: number | null | undefined,
    clearRule: () => void,
  ) => {
    if (isRuleOwnershipRefusal(e) && typeof ruleId === 'number') {
      setUnbindableRules((prev) => (prev.includes(ruleId) ? prev : [...prev, ruleId]));
      clearRule();
      setError(
        `Execution rule #${ruleId} was created under a different GeoLink `
        + 'account, so it cannot be bound here. The selection has been '
        + 'cleared — save again, or choose a rule created by this account.',
      );
      return;
    }
    setError(humanError(e));
  }, []);

  /**
   * Mirror whether the object talks onto its metadata.
   *
   * Same reason the flag is mirrored: the map reads `metadata` and never
   * the presence config, so a hover card had no way to know an object
   * was conversational and said so — every building read "Talks —", and
   * the visitor was left to guess. GeoLink's agent row stays
   * authoritative for whether a conversation is actually allowed; this
   * copy only tells the map what to offer.
   *
   * Deliberately thin. Persona, prompts and audience rules are the
   * operator's business and are not published to every passer-by.
   */
  const mirrorAgentToMetadata = useCallback(async (saved: AgentDraft) => {
    try {
      const existing = await geoLinkSpatialObjectsAdminClient.getObject(objectId);
      const meta = { ...((existing?.metadata ?? {}) as Record<string, unknown>) };

      if (saved.enabled) {
        meta.ai_presence = {
          enabled: true,
          mode: saved.interactionMode ?? null,
          proximityRadiusMeters: saved.proximityRadiusMeters ?? null,
          autoInitiate: !!saved.autoInitiate,
        };
      } else {
        // Recorded as off rather than removed: "switched off" and "never
        // configured" are different answers, and the card renders them
        // differently.
        meta.ai_presence = { enabled: false };
      }
      await geoLinkSpatialObjectsAdminClient.updateObject(objectId, { metadata: meta });
      try {
        window.dispatchEvent(new CustomEvent('geolink:spatial-refresh'));
      } catch { /* no window in tests */ }
    } catch {
      // The configuration itself saved. A stale hover card is worth far
      // less than the save it would otherwise appear to have undone.
    }
  }, [objectId]);

  const saveAgent = useCallback(async () => {
    setError(null); setNotice(null); setMsgScope('agent');
    try {
      const saved = await geoLinkSpatialObjectsAdminClient.putAgentConfig(objectId, {
        ...agent,
        // Recording the wallet is what makes autonomous behaviour
        // attributable; without it the object could act with nobody
        // accountable for it.
        authorizedByWallet: walletAddress ?? null,
      });
      setAgentSaved(saved);
      setAgent(saved);
      await mirrorAgentToMetadata(saved);
      setNotice('AI presence saved.');
    } catch (e) {
      reportSaveFailure(e, agent.executionRuleId, () => setA('executionRuleId', null));
    }
  }, [agent, objectId, walletAddress, reportSaveFailure, setA, mirrorAgentToMetadata]);

  const saveMedia = useCallback(async () => {
    setError(null); setNotice(null); setMsgScope('media');
    try {
      const saved = await geoLinkSpatialObjectsAdminClient.putMediaConfig(objectId, {
        ...media,
        authorizedByWallet: walletAddress ?? null,
      });
      setMediaSaved(saved);
      setMedia(saved);
      setNotice('Media presence saved.');
    } catch (e) {
      reportSaveFailure(e, media.executionRuleId, () => setM('executionRuleId', null));
    }
  }, [media, objectId, walletAddress, reportSaveFailure, setM]);

  // ── Country flag ────────────────────────────────────────────────────
  //
  // A flag is just approved content with a well-known id, so it needs no
  // separate storage — but typing `country-flag:AE` by hand is not a
  // feature, and getting the country wrong is easy. This resolves the
  // country, offers one switch, and keeps the allow-list honest.

  const flagIso = useMemo(() => {
    const refs = Array.isArray(surface.allowedContentRefs) ? surface.allowedContentRefs : [];
    for (const r of [surface.defaultContentRef, ...refs]) {
      const iso = parseFlagContentRef(typeof r === 'string' ? r : r?.ref);
      if (iso) return iso;
    }
    return null;
  }, [surface.allowedContentRefs, surface.defaultContentRef]);

  /**
   * The country whose flag is being chosen, which is not necessarily the
   * country the building stands in. The object's own country is only the
   * starting suggestion.
   */
  const [flagPick, setFlagPick] = useState<string | null>(() => {
    const stored = String((storedFlag?.countryCode ?? '') || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(stored) ? stored : null;
  });

  const resolvedCountry = useMemo(() => {
    const s = String(flagIso || flagPick || countryCode || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(s) ? s : null;
  }, [flagIso, flagPick, countryCode]);

  /**
   * Reset to what the object records when the editor moves to another one.
   *
   * Keyed on the stored values rather than the prop object, which is a
   * fresh literal on every parent render and would throw away a choice the
   * operator had just made.
   */
  const storedFlagKey = [
    objectId,
    String(storedFlag?.countryCode ?? ''),
    String(storedFlag?.arrangement ?? ''),
  ].join('|');
  useEffect(() => {
    const [, iso, arrangement] = storedFlagKey.split('|');
    const code = iso.trim().toUpperCase();
    setFlagPick(/^[A-Z]{2}$/.test(code) ? code : null);
    setArrangementId(arrangement || null);
  }, [storedFlagKey]);

  /** The flag itself, read once per country and then cached. */
  const [flagReading, setFlagReading] = useState<FlagReading | null>(null);
  useEffect(() => {
    let live = true;
    if (!resolvedCountry) { setFlagReading(null); return () => { live = false; }; }
    void getFlagReading(resolvedCountry).then((r) => { if (live) setFlagReading(r); });
    return () => { live = false; };
  }, [resolvedCountry]);

  const arrangements = useMemo<FlagArrangement[]>(
    () => (flagReading ? flagArrangements(flagReading) : []),
    [flagReading],
  );

  /** Null means "whichever is recommended", which follows the country. */
  const [arrangementId, setArrangementId] = useState<string | null>(() => {
    const stored = storedFlag?.arrangement;
    return typeof stored === 'string' && stored ? stored : null;
  });

  const chosenArrangement = useMemo(() => (
    arrangements.find((a) => a.id === arrangementId) ?? arrangements[0] ?? null
  ), [arrangements, arrangementId]);

  /** The bands that will actually be painted, top of the building first. */
  const flagBands = useMemo(() => chosenArrangement?.bands ?? [], [chosenArrangement]);

  const setFlag = useCallback((on: boolean, iso?: string | null) => {
    const country = String(iso ?? resolvedCountry ?? '').trim().toUpperCase();
    setSurface((prev) => {
      const refs = (Array.isArray(prev.allowedContentRefs) ? prev.allowedContentRefs : [])
        .filter((r) => !parseFlagContentRef(typeof r === 'string' ? r : r?.ref));
      const prevDefaultWasFlag = !!parseFlagContentRef(prev.defaultContentRef);

      if (!on || !/^[A-Z]{2}$/.test(country)) {
        return {
          ...prev,
          allowedContentRefs: refs,
          // Only clear the default if the flag was what it pointed at.
          defaultContentRef: prevDefaultWasFlag ? null : prev.defaultContentRef,
        };
      }

      const ref = flagContentRef(country);
      return {
        ...prev,
        enabled: true,
        // A flag on a building is the facade, not a screen bolted to it.
        surfaceKind: 'facade',
        allowedContentRefs: [...refs, ref],
        // Default rather than requiring an activation: "show our flag" is a
        // standing state, not an event. An occasion can still override it.
        defaultContentRef: ref,
      };
    });
  }, [resolvedCountry]);

  /**
   * Mirror the flag intent onto the object's metadata.
   *
   * The map reads `metadata` but never the surface config: neither
   * `/render` nor `/api/map` carries it, and asking for it per visible
   * building would be one request per tower. GeoLink's surface row stays
   * authoritative for *whether the activation is allowed*; this copy only
   * tells the renderer what to draw, and is merged so nothing else in the
   * bag is lost.
   */
  const mirrorFlagToMetadata = useCallback(async (iso: string | null) => {
    const existing = await geoLinkSpatialObjectsAdminClient.getObject(objectId);
    const meta = { ...((existing?.metadata ?? {}) as Record<string, unknown>) };

    // The bands travel with the intent, and are what the map paints. The
    // arrangement id is not enough on its own: it would have to be turned
    // back into colours against a palette cache that may be cold, so the
    // building would be drawn in some other arrangement — or not at all —
    // until the flag happened to be read again. Sending the colours means
    // the tower shows exactly what was previewed.
    const bands = iso ? flagBands : [];
    // The layout travels too, because the colours alone cannot say whether
    // they go round the building or down it. A vertical arrangement stored
    // as bands would come back as the wrong building.
    const layout = iso ? chosenArrangement?.layout ?? null : null;
    if (iso) {
      meta.surface_flag = {
        enabled: true,
        countryCode: iso,
        arrangement: chosenArrangement?.id ?? null,
        bands,
        ...(layout ? { layout } : {}),
      };
    } else {
      delete meta.surface_flag;
    }
    await geoLinkSpatialObjectsAdminClient.updateObject(objectId, { metadata: meta });

    // The viewport response is a summary with no metadata at all, so the
    // map would only learn about this flag if someone clicked the building
    // — and would forget it again on the next refresh. Telling the render
    // cache first-hand is what makes the change appear and stay.
    rememberFlagIntent(objectId, iso, {
      arrangement: chosenArrangement?.id ?? null,
      bands,
      layout,
    });

    // A soft refresh re-normalises the viewport, which is what actually
    // repaints the facade. Without it the operator waits up to 30s and
    // reasonably concludes the button did nothing.
    try {
      window.dispatchEvent(new CustomEvent('geolink:spatial-refresh'));
    } catch { /* no window in tests */ }
  }, [objectId, flagBands, chosenArrangement]);

  const saveSurface = useCallback(async () => {
    setError(null); setNotice(null); setMsgScope('surface');

    // GeoLink refuses out-of-range numbers as `invalid_configuration`,
    // which names nothing. Saying which box is wrong costs a round trip
    // less and reads like an answer.
    const complaint = surfaceNumbersComplaint(surface);
    if (complaint) { setError(complaint); return; }

    try {
      const saved = await geoLinkSpatialObjectsAdminClient.putSurfaceConfig(objectId, {
        ...surface,
        authorizedByWallet: walletAddress ?? null,
      });
      setSurfaceSaved(saved);
      setSurface(saved);

      // The render hint is secondary: a failure here leaves the object
      // configured and merely undrawn, which is worth saying rather than
      // reporting the whole save as failed.
      let mirrorFailed = false;
      try {
        await mirrorFlagToMetadata(flagIso);
      } catch {
        mirrorFailed = true;
      }
      setNotice(mirrorFailed
        ? 'Surface presence saved, but the map could not be told about the flag. Save again to retry.'
        : 'Surface presence saved.');
    } catch (e) {
      reportSaveFailure(e, surface.executionRuleId, () => setS('executionRuleId', null));
    }
  }, [surface, objectId, walletAddress, reportSaveFailure, setS, flagIso, mirrorFlagToMetadata]);

  /**
   * Show the configured content now.
   *
   * Saving describes what is permitted; this is what makes something
   * appear. Without it the only way to see a facade change was to wait for
   * a trigger that may never fire.
   */
  const activateSurfaceNow = useCallback(async () => {
    setError(null); setNotice(null); setMsgScope('surface');
    const ref = surface.defaultContentRef
      || (Array.isArray(surface.allowedContentRefs) && surface.allowedContentRefs.length > 0
        ? (typeof surface.allowedContentRefs[0] === 'string'
          ? surface.allowedContentRefs[0]
          : surface.allowedContentRefs[0].ref)
        : null);
    if (!ref) {
      setError('Nothing to show: approve some content or pick a flag first.');
      return;
    }
    const result = await activateSpatialSurface(objectId, {
      contentRef: ref,
      wallet: walletAddress,
      reason: 'operator_preview',
    });
    if (!result.ok) {
      setError(failureText(result));
      return;
    }

    // Activation is recorded server-side, but the map decides what to draw
    // from the object's metadata, so without the mirror this button said
    // "showing" while the building stayed exactly as it was.
    const iso = parseFlagContentRef(ref);
    if (iso) {
      try {
        await mirrorFlagToMetadata(iso);
      } catch {
        setNotice(`Showing ${ref}, but the map could not be told. Save the section to retry.`);
        return;
      }
    }
    setNotice(iso
      ? `Showing ${ref}. The building is being repainted now.`
      : `Showing ${ref}. The map picks it up on its next refresh.`);
  }, [
    objectId, surface.defaultContentRef, surface.allowedContentRefs,
    walletAddress, mirrorFlagToMetadata,
  ]);

  const radiusWarning = useMemo(() => {
    const enter = Number(media.playbackRadiusMeters ?? 0);
    const exit = Number(media.exitRadiusMeters ?? 0);
    if (exit <= enter) {
      return 'The exit radius must be larger than the playback radius, or availability will flicker for anyone standing on the boundary.';
    }
    return null;
  }, [media.playbackRadiusMeters, media.exitRadiusMeters]);

  if (!objectId) {
    return (
      <div className="spatial-admin-section">
        <h3>AI &amp; media presence</h3>
        <div className="spatial-admin-helper">
          Save the object first. Presence is configured against a stored
          object so the settings have something to belong to.
        </div>
      </div>
    );
  }

  return (
    <>
      {msgScope === null && (error || notice) && (
        <div className="spatial-admin-section">
          <Feedback error={error} notice={notice} />
        </div>
      )}

      {/* ── Treasury ─────────────────────────────────────────────────
          Reads the chain, so it is mounted only once the object has an
          id there is any point asking about. */}
      {objectId && (
        <SpatialObjectTreasurySection
          objectId={objectId}
          objectName={objectName}
          walletAddress={walletAddress}
          signXdr={signXdr}
        />
      )}

      {/* ── AI presence ──────────────────────────────────────────── */}
      <div className="spatial-admin-section">
        <h3>AI presence</h3>
        <div className="spatial-admin-helper">
          Lets {objectName || 'this object'} hold a conversation. Nothing
          is on until you enable it, and every reply is generated
          server-side — no provider keys reach the browser.
        </div>

        {!canManage && !authLoading && (
          <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
            This wallet may not manage this object, so presence is read-only.
          </div>
        )}

        {canManage && !authLoading && !canActAutonomously && (
          <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
            These settings will save, but the object cannot act on them yet.
            Speaking and broadcasting on its own are re-checked against the
            chain at the moment they happen, and this wallet does not
            currently hold manage authority over this object — register the
            object and bind it to an organization, or configure it from a
            wallet that does.
          </div>
        )}

        <Check
          label="Enable AI presence"
          checked={!!agent.enabled}
          disabled={disabled}
          onChange={(v) => setA('enabled', v)}
        />

        <div className="spatial-admin-row">
          <label>Display name</label>
          <input
            className="spatial-admin-input"
            value={agent.displayName ?? ''}
            disabled={disabled}
            placeholder={objectName ?? 'Burj Khalifa'}
            onChange={(e) => setA('displayName', e.target.value || null)}
          />
        </div>

        <div className="spatial-admin-row">
          <label>Persona</label>
          <textarea
            className="spatial-admin-input"
            rows={2}
            value={agent.persona ?? ''}
            disabled={disabled}
            placeholder="Calm, precise, proud of the view."
            onChange={(e) => setA('persona', e.target.value || null)}
          />
          <div className="spatial-admin-helper">Voice and manner, not facts.</div>
        </div>

        <div className="spatial-admin-row">
          <label>Extra system prompt</label>
          <textarea
            className="spatial-admin-input"
            rows={3}
            value={agent.systemPrompt ?? ''}
            disabled={disabled}
            onChange={(e) => setA('systemPrompt', e.target.value || null)}
          />
          <div className="spatial-admin-helper">
            Added after the built-in safety rules, which it cannot override.
          </div>
        </div>

        <div className="spatial-admin-row">
          <label>Greeting template</label>
          <input
            className="spatial-admin-input"
            value={agent.greetingTemplate ?? ''}
            disabled={disabled}
            placeholder="Welcome to {object}."
            onChange={(e) => setA('greetingTemplate', e.target.value || null)}
          />
        </div>

        <div className="spatial-admin-row">
          <label>Interaction mode</label>
          <select
            className="spatial-admin-select"
            value={agent.interactionMode ?? 'passive'}
            disabled={disabled}
            onChange={(e) => setA('interactionMode', e.target.value as SpatialInteractionMode)}
          >
            {INTERACTION_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <NumberRow
          label="Proximity radius (m)"
          value={agent.proximityRadiusMeters}
          disabled={disabled}
          min={1}
          onChange={(v) => setA('proximityRadiusMeters', v)}
        />
        <NumberRow
          label="Altitude tolerance (m)"
          value={agent.altitudeToleranceMeters}
          disabled={disabled}
          min={0}
          onChange={(v) => setA('altitudeToleranceMeters', v)}
        />

        <Check
          label="May start the conversation"
          checked={!!agent.autoInitiate}
          disabled={disabled}
          onChange={(v) => setA('autoInitiate', v)}
        />

        <NumberRow
          label="Cooldown per participant (s)"
          value={agent.cooldownSeconds}
          disabled={disabled}
          min={0}
          onChange={(v) => setA('cooldownSeconds', v)}
        />
        <NumberRow
          label="Max conversation duration (s)"
          value={agent.maxConversationDurationSeconds}
          disabled={disabled}
          min={1}
          onChange={(v) => setA('maxConversationDurationSeconds', v)}
        />
        <NumberRow
          label="Max messages per session"
          value={agent.maxMessagesPerSession}
          disabled={disabled}
          min={1}
          hint="Bounds cost even if the configuration is later over-broad."
          onChange={(v) => setA('maxMessagesPerSession', v)}
        />

        <Check
          label="Humans may talk to it"
          checked={agent.allowHumanUsers !== false}
          disabled={disabled}
          onChange={(v) => setA('allowHumanUsers', v)}
        />
        <Check
          label="AI agents may talk to it"
          checked={!!agent.allowAiAgents}
          disabled={disabled}
          onChange={(v) => setA('allowAiAgents', v)}
        />
        <Check
          label="Require a connected wallet"
          checked={!!agent.requireConnectedWallet}
          disabled={disabled}
          onChange={(v) => setA('requireConnectedWallet', v)}
        />
        <Check
          label="Respect country policy"
          checked={agent.countryPolicyAware !== false}
          disabled={disabled}
          onChange={(v) => setA('countryPolicyAware', v)}
        />

        <RuleSelect
          label="Execution rule"
          value={agent.executionRuleId}
          rules={rules}
          unbindable={unbindableRules}
          disabled={disabled}
          hint="With interaction mode execution_rule, conversations happen only while this rule passes."
          onChange={(v) => setA('executionRuleId', v)}
        />

        <div className="spatial-admin-quickbar">
          <button className="spatial-admin-button primary" disabled={disabled} onClick={() => void saveAgent()}>
            Save AI presence
          </button>
          <button className="spatial-admin-button" disabled={loading} onClick={() => void load()}>
            ↻ Reload
          </button>
        </div>
        {msgScope === 'agent' && <Feedback error={error} notice={notice} />}

        {agentSaved?.authorizedByWallet && (
          <div className="spatial-admin-helper">
            Autonomous behaviour authorized by {agentSaved.authorizedByWallet.slice(0, 6)}…
            {agentSaved.authorizedByWallet.slice(-4)}
            {agentSaved.authorizedAt ? ` on ${new Date(agentSaved.authorizedAt).toLocaleString()}` : ''}.
            Re-checked against the chain each time the object acts.
          </div>
        )}
      </div>

      {/* ── Media / live ─────────────────────────────────────────── */}
      <div className="spatial-admin-section">
        <h3>Media &amp; live</h3>
        <div className="spatial-admin-helper">
          Broadcasting, speech and nearby playback. A spatial broadcast is
          an ordinary live session, so the country WebRTC policy, the
          deny-list and duration caps apply exactly as they do to a person.
        </div>

        <Check
          label="Enable media presence"
          checked={!!media.enabled}
          disabled={disabled}
          onChange={(v) => setM('enabled', v)}
        />

        <h4 style={{ margin: '10px 0 4px', fontSize: 12, textTransform: 'uppercase', color: '#6b7280' }}>Broadcast</h4>

        <Check
          label="Allow live broadcasting"
          checked={!!media.allowLiveBroadcast}
          disabled={disabled}
          onChange={(v) => setM('allowLiveBroadcast', v)}
        />
        <div className="spatial-admin-row">
          <label>Trigger mode</label>
          <select
            className="spatial-admin-select"
            value={media.broadcastTriggerMode ?? 'manual'}
            disabled={disabled}
            onChange={(e) => setM('broadcastTriggerMode', e.target.value as SpatialBroadcastTriggerMode)}
          >
            {TRIGGER_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <div className="spatial-admin-helper">
            ai_action lets the object&apos;s AI request a start. The request
            is still authorized, policy-checked and rule-checked before
            anything goes live.
          </div>
        </div>
        <Check
          label="Start automatically when triggered"
          checked={!!media.autoStartBroadcast}
          disabled={disabled}
          onChange={(v) => setM('autoStartBroadcast', v)}
        />
        <div className="spatial-admin-row">
          <label>Title template</label>
          <input
            className="spatial-admin-input"
            value={media.broadcastTitleTemplate ?? ''}
            disabled={disabled}
            placeholder="{object} Live"
            onChange={(e) => setM('broadcastTitleTemplate', e.target.value || null)}
          />
        </div>
        <div className="spatial-admin-row">
          <label>Description template</label>
          <input
            className="spatial-admin-input"
            value={media.broadcastDescriptionTemplate ?? ''}
            disabled={disabled}
            onChange={(e) => setM('broadcastDescriptionTemplate', e.target.value || null)}
          />
        </div>
        <div className="spatial-admin-row">
          <label>Visibility</label>
          <select
            className="spatial-admin-select"
            value={media.visibility ?? 'public'}
            disabled={disabled}
            onChange={(e) => setM('visibility', e.target.value as 'public' | 'groups')}
          >
            <option value="public">public</option>
            <option value="groups">groups</option>
          </select>
        </div>
        <NumberRow
          label="Max broadcast duration (s)"
          value={media.maxBroadcastDurationSeconds}
          disabled={disabled}
          min={1}
          onChange={(v) => setM('maxBroadcastDurationSeconds', v)}
        />
        <NumberRow
          label="Idle timeout (s)"
          value={media.idleTimeoutSeconds}
          disabled={disabled}
          min={1}
          hint="Ends a broadcast nobody is listening to and nothing is speaking into."
          onChange={(v) => setM('idleTimeoutSeconds', v)}
        />
        <div className="spatial-admin-row">
          <label>Source anchor</label>
          <select
            className="spatial-admin-select"
            value={media.broadcastAnchorType ?? 'centroid'}
            disabled={disabled}
            onChange={(e) => setM('broadcastAnchorType', e.target.value as SpatialBroadcastAnchorType)}
          >
            {ANCHOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="spatial-admin-helper">
            Where the broadcast originates. For a tower the centroid is not
            the entrance, and neither is whichever pavement a phone happened
            to report.
          </div>
        </div>

        <h4 style={{ margin: '10px 0 4px', fontSize: 12, textTransform: 'uppercase', color: '#6b7280' }}>Speech</h4>

        <Check
          label="Speak AI responses"
          checked={!!media.ttsEnabled}
          disabled={disabled}
          onChange={(v) => setM('ttsEnabled', v)}
        />
        <div className="spatial-admin-row">
          <label>Voice</label>
          <input
            className="spatial-admin-input"
            list="spatial-voice-hints"
            value={media.voiceId ?? ''}
            disabled={disabled}
            placeholder="en-US-JennyNeural"
            onChange={(e) => setM('voiceId', e.target.value || null)}
          />
          <datalist id="spatial-voice-hints">
            {VOICE_HINTS.map((v) => <option key={v} value={v} />)}
          </datalist>
        </div>
        <div className="spatial-admin-row">
          <label>Language</label>
          <input
            className="spatial-admin-input"
            value={media.language ?? ''}
            disabled={disabled}
            placeholder="en-US"
            onChange={(e) => setM('language', e.target.value || null)}
          />
        </div>
        <div className="spatial-admin-row">
          <label>Speaking style</label>
          <input
            className="spatial-admin-input"
            value={media.speakingStyle ?? ''}
            disabled={disabled}
            placeholder="newscast"
            onChange={(e) => setM('speakingStyle', e.target.value || null)}
          />
        </div>
        <NumberRow
          label="Playback rate"
          value={media.playbackRate}
          disabled={disabled}
          min={0.25}
          step={0.05}
          hint="Applied by the listener's player, not baked into the audio."
          onChange={(v) => setM('playbackRate', v)}
        />
        <NumberRow
          label="Max characters per utterance"
          value={media.maxSpeechCharacters}
          disabled={disabled}
          min={1}
          onChange={(v) => setM('maxSpeechCharacters', v)}
        />
        <div className="spatial-admin-row">
          <label>When speech arrives mid-utterance</label>
          <select
            className="spatial-admin-select"
            value={media.interruptMode ?? 'queue'}
            disabled={disabled}
            onChange={(e) => setM('interruptMode', e.target.value as SpatialTtsInterruptMode)}
          >
            {INTERRUPT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <h4 style={{ margin: '10px 0 4px', fontSize: 12, textTransform: 'uppercase', color: '#6b7280' }}>Nearby playback</h4>

        <Check
          label="Discoverable near the object"
          checked={!!media.proximityPlaybackEnabled}
          disabled={disabled}
          onChange={(v) => setM('proximityPlaybackEnabled', v)}
        />
        <NumberRow
          label="Playback radius (m)"
          value={media.playbackRadiusMeters}
          disabled={disabled}
          min={1}
          onChange={(v) => setM('playbackRadiusMeters', v)}
        />
        <NumberRow
          label="Exit radius (m)"
          value={media.exitRadiusMeters}
          disabled={disabled}
          min={1}
          hint="Must exceed the playback radius. The gap is what stops GPS jitter from flapping availability."
          onChange={(v) => setM('exitRadiusMeters', v)}
        />
        {radiusWarning && (
          <div className="spatial-admin-helper" style={{ color: '#b45309' }}>{radiusWarning}</div>
        )}
        <NumberRow
          label="Altitude tolerance (m)"
          value={media.altitudeToleranceMeters}
          disabled={disabled}
          min={0}
          onChange={(v) => setM('altitudeToleranceMeters', v)}
        />
        <Check
          label="Floor aware"
          checked={!!media.floorAware}
          disabled={disabled}
          hint="Requires the listener's altitude to fall inside the floor band."
          onChange={(v) => setM('floorAware', v)}
        />
        <Check
          label="Zone aware"
          checked={!!media.zoneAware}
          disabled={disabled}
          onChange={(v) => setM('zoneAware', v)}
        />
        <Check
          label="Surface the player automatically"
          checked={media.autoSurfacePlayer !== false}
          disabled={disabled}
          onChange={(v) => setM('autoSurfacePlayer', v)}
        />
        <Check
          label="Start audio without asking"
          checked={!!media.autoPlayAudio}
          disabled={disabled}
          hint="Advisory only. Browsers require a gesture before audio; the player falls back to a Listen button."
          onChange={(v) => setM('autoPlayAudio', v)}
        />
        <div className="spatial-admin-row">
          <label>Distance volume curve</label>
          <select
            className="spatial-admin-select"
            value={media.volumeCurve ?? 'smoothstep'}
            disabled={disabled}
            onChange={(e) => setM('volumeCurve', e.target.value as SpatialVolumeCurve)}
          >
            {VOLUME_CURVES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Check
          label="Stop when the listener leaves"
          checked={media.stopOnExit !== false}
          disabled={disabled}
          onChange={(v) => setM('stopOnExit', v)}
        />
        <Check
          label="Fade out on exit"
          checked={media.fadeOnExit !== false}
          disabled={disabled}
          onChange={(v) => setM('fadeOnExit', v)}
        />
        <NumberRow
          label="Cooldown per participant (s)"
          value={media.cooldownSeconds}
          disabled={disabled}
          min={0}
          onChange={(v) => setM('cooldownSeconds', v)}
        />

        <h4 style={{ margin: '10px 0 4px', fontSize: 12, textTransform: 'uppercase', color: '#6b7280' }}>Audience</h4>

        <Check
          label="Humans may listen"
          checked={media.allowHumanListeners !== false}
          disabled={disabled}
          onChange={(v) => setM('allowHumanListeners', v)}
        />
        <Check
          label="AI agents may listen"
          checked={!!media.allowAiAgentListeners}
          disabled={disabled}
          hint="Agents receive the transcript rather than audio; there is no point making a machine listen to speech it can read."
          onChange={(v) => setM('allowAiAgentListeners', v)}
        />
        <Check
          label="Require a connected wallet"
          checked={!!media.requireConnectedWallet}
          disabled={disabled}
          onChange={(v) => setM('requireConnectedWallet', v)}
        />
        <Check
          label="Respect country policy"
          checked={media.countryPolicyAware !== false}
          disabled={disabled}
          onChange={(v) => setM('countryPolicyAware', v)}
        />

        <RuleSelect
          label="Execution rule"
          value={media.executionRuleId}
          rules={rules}
          unbindable={unbindableRules}
          disabled={disabled}
          hint="With trigger mode execution_rule, the broadcast runs only while this rule passes."
          onChange={(v) => setM('executionRuleId', v)}
        />

        <div className="spatial-admin-quickbar">
          <button className="spatial-admin-button primary" disabled={disabled} onClick={() => void saveMedia()}>
            Save media presence
          </button>
          <button className="spatial-admin-button" disabled={loading} onClick={() => void load()}>
            ↻ Reload
          </button>
        </div>
        {msgScope === 'media' && <Feedback error={error} notice={notice} />}

        {assets.length > 0 && (
          <div className="spatial-admin-row">
            <label>Attached recordings</label>
            <div className="spatial-admin-helper">
              {assets.map((a) => (
                <div key={a.id}>
                  {a.title || a.recordingId} · {a.kind}
                  {a.durationMs ? ` · ${Math.round(a.durationMs / 1000)}s` : ''}
                </div>
              ))}
            </div>
          </div>
        )}

        {mediaSaved && !mediaSaved.enabled && (
          <div className="spatial-admin-helper">
            Saved but disabled — nothing will broadcast until media presence is enabled.
          </div>
        )}
      </div>

      {/* ── Surface presence ─────────────────────────────────────── */}
      <div className="spatial-admin-section">
        <h3>Surface</h3>
        <div className="spatial-admin-helper">
          What may be shown on this object&apos;s facade, screen or
          lighting. Independent of broadcasting: a surface can light up
          in silence, and the object can speak with the surface dark.
        </div>

        <Check
          label="Enable surface presence"
          checked={!!surface.enabled}
          disabled={disabled}
          onChange={(v) => setS('enabled', v)}
        />

        <div className="spatial-admin-row">
          <label>Surface kind</label>
          <select
            className="spatial-admin-select"
            value={surface.surfaceKind ?? 'facade'}
            disabled={disabled}
            onChange={(e) => setS('surfaceKind', e.target.value as SpatialSurfaceKind)}
          >
            {SURFACE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>

        <div className="spatial-admin-row">
          <label>Activation mode</label>
          <select
            className="spatial-admin-select"
            value={surface.activationMode ?? 'manual'}
            disabled={disabled}
            onChange={(e) => setS('activationMode', e.target.value as SpatialSurfaceActivationMode)}
          >
            {SURFACE_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div className="spatial-admin-row">
          <label>Approved content</label>
          <textarea
            className="spatial-admin-input"
            rows={4}
            value={contentRefsToText(surface.allowedContentRefs)}
            disabled={disabled}
            placeholder={'uae-national-day\nnew-year-countdown'}
            onChange={(e) => setS('allowedContentRefs', textToContentRefs(e.target.value))}
          />
          <div className="spatial-admin-helper">
            One id per line. Anything not on this list is refused, including
            content an AI proposes — an empty list means nothing may be shown.
          </div>
        </div>

        <div className="spatial-admin-row">
          <label>Default content</label>
          <input
            className="spatial-admin-input"
            value={surface.defaultContentRef ?? ''}
            disabled={disabled}
            placeholder="(nothing)"
            onChange={(e) => setS('defaultContentRef', e.target.value || null)}
          />
          <div className="spatial-admin-helper">Shown when nothing has been activated.</div>
        </div>

        {/* ── Country flag ─────────────────────────────────────────── */}
        <div className="spatial-admin-row">
          <label htmlFor="spatial-flag-country">Flag country</label>
          <select
            id="spatial-flag-country"
            className="spatial-admin-input"
            value={resolvedCountry ?? ''}
            disabled={disabled}
            style={{ maxWidth: 280 }}
            onChange={(e) => {
              const next = e.target.value || null;
              setFlagPick(next);
              // A country the flag has never been read for has no
              // arrangement yet; keeping the old id would silently pick
              // whichever of the new flag's options happens to share it.
              setArrangementId(null);
              if (flagIso && next) setFlag(true, next);
            }}
          >
            <option value="">(choose a country)</option>
            {COUNTRY_CHOICES.map((c) => (
              <option key={c.code} value={c.code}>{`${c.name} (${c.code})`}</option>
            ))}
          </select>
          <div className="spatial-admin-helper">
            Any building may fly any flag — an embassy wears the flag it
            represents, and a tower can mark another country’s national day.
            {countryCode && resolvedCountry && resolvedCountry !== countryCode
              ? ` This object stands in ${countryCode}.`
              : ''}
          </div>
        </div>

        <Check
          label="Show the country flag on this building"
          checked={!!flagIso}
          disabled={disabled || !resolvedCountry}
          hint={resolvedCountry
            ? `Approves ${flagContentRef(resolvedCountry)} and makes it the default. The colours are read from the flag itself and painted onto the building in whichever arrangement is chosen below.`
            : 'Choose a country above first — that is the flag this would show.'}
          onChange={(v) => setFlag(v, resolvedCountry)}
        />

        {!!flagIso && (
          <div className="spatial-admin-row">
            <label>Arrangement</label>
            {arrangements.length === 0 ? (
              <div className="spatial-admin-helper">
                {flagReading
                  ? 'The flag’s colours could not be read, so nothing will be painted.'
                  : 'Reading the flag…'}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
                  {arrangements.map((a, i) => {
                    const selected = chosenArrangement?.id === a.id;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        disabled={disabled}
                        title={`${a.label} — ${a.note}`}
                        aria-pressed={selected}
                        onClick={() => setArrangementId(a.id)}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center',
                          gap: 4, padding: 6, width: 96, cursor: disabled ? 'default' : 'pointer',
                          background: selected ? '#eff6ff' : '#fff',
                          border: `2px solid ${selected ? '#2563eb' : '#e2e8f0'}`,
                          borderRadius: 6, textAlign: 'center',
                        }}
                      >
                        <ArrangementPreview layout={a.layout} />
                        <span style={{ fontSize: 10, lineHeight: 1.2 }}>
                          {i === 0 ? `${a.label} ★` : a.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="spatial-admin-helper">
                  {chosenArrangement
                    ? `${chosenArrangement.note} ${layoutShapeNote(chosenArrangement.layout)} ★ marks the recommendation; saving this section is what repaints the building.`
                    : ''}
                </div>
              </>
            )}
          </div>
        )}

        <NumberRow
          label="Maximum activation (s)"
          value={surface.maxActivationSeconds}
          disabled={disabled}
          min={1}
          hint="A facade left lit by a crashed process is a real-world consequence, so every activation expires."
          onChange={(v) => setS('maxActivationSeconds', v)}
        />
        <NumberRow
          label="Cooldown (s)"
          value={surface.cooldownSeconds}
          disabled={disabled}
          min={0}
          onChange={(v) => setS('cooldownSeconds', v)}
        />

        <NumberRow
          label="Orientation (°)"
          value={surface.orientationDegrees}
          disabled={disabled}
          min={0}
          clearable
          hint="Which way the surface faces. Advisory — the renderer decides what it can draw. Leave empty if it doesn't matter."
          onChange={(v) => setS('orientationDegrees', v)}
        />
        <NumberRow
          label="Width (m)"
          value={surface.widthMeters}
          disabled={disabled}
          min={0}
          clearable
          hint="Empty means unspecified — GeoLink rejects a width of zero. A flag painted on the facade uses the building, not these dimensions."
          onChange={(v) => setS('widthMeters', v)}
        />
        <NumberRow
          label="Height (m)"
          value={surface.heightMeters}
          disabled={disabled}
          min={0}
          clearable
          hint="Empty means unspecified. Clear the box rather than typing 0."
          onChange={(v) => setS('heightMeters', v)}
        />

        <RuleSelect
          label="Execution rule"
          value={surface.executionRuleId}
          rules={rules}
          unbindable={unbindableRules}
          disabled={disabled}
          hint="With activation mode execution_rule, the surface lights only while this rule passes."
          onChange={(v) => setS('executionRuleId', v)}
        />

        <div className="spatial-admin-quickbar">
          <button className="spatial-admin-button primary" disabled={disabled} onClick={() => void saveSurface()}>
            Save surface presence
          </button>
          <button
            className="spatial-admin-button"
            disabled={disabled || !surfaceSaved?.enabled}
            title={surfaceSaved?.enabled
              ? 'Show the default content now'
              : 'Save the section with surface presence enabled first'}
            onClick={() => void activateSurfaceNow()}
          >
            Show it now
          </button>
        </div>
        {!disabled && !surfaceSaved?.enabled && (
          <div className="spatial-admin-helper">
            {surface.enabled
              ? '“Show it now” waits on a save: nothing may be lit until the saved configuration says the surface is enabled.'
              : 'Tick “Enable surface presence” and save before anything can be shown.'}
          </div>
        )}
        {msgScope === 'surface' && <Feedback error={error} notice={notice} />}

        {surfaceSaved && surfaceSaved.enabled
          && (!surfaceSaved.allowedContentRefs || surfaceSaved.allowedContentRefs.length === 0)
          && !surfaceSaved.defaultContentRef && (
          <div className="spatial-admin-helper" style={{ color: '#b45309' }}>
            Enabled with nothing approved — every activation will be refused
            until at least one content id is listed.
          </div>
        )}
      </div>
    </>
  );
};

export default SpatialPresenceSections;
